import type { QueryDocumentSnapshot } from 'firebase/firestore';
import { COLLECTIONS } from '../config/collections';
import type { OrganizationMember } from '../types/firestore';

/**
 * Convert a collectionGroup('members') snapshot doc into an OrganizationMember.
 *
 * Legacy writers (e.g. functions/src/authTriggers.ts before the
 * OFFICEHUB-CLIENT-2D fix) created member docs without organizationId or
 * memberId. Both are recoverable from the document itself: the path is
 * organizations/{orgId}/members/{uid} and the doc id is the memberId by
 * convention. Without this recovery, doc(db, 'organizations', undefined)
 * throws and takes down the entire org fetch for the user.
 *
 * Returns null (and warns) only for docs whose organizationId cannot be
 * resolved at all — i.e. members docs not under the organizations collection.
 */
export function toMembership(snapshot: QueryDocumentSnapshot): OrganizationMember | null {
  const data = snapshot.data() as OrganizationMember;

  // The path is ground truth: a doc can only live under the org that owns it,
  // while the stored field is writable by org admins with no rules validation.
  // Prefer the path so a crafted/contradicting stored value can't point the
  // membership at a different org.
  const parentDoc = snapshot.ref.parent.parent;
  const pathOrgId =
    parentDoc?.parent.id === COLLECTIONS.ORGANIZATIONS ? parentDoc.id : undefined;
  if (pathOrgId && data.organizationId && data.organizationId !== pathOrgId) {
    console.warn(
      `[toMembership] Stored organizationId "${data.organizationId}" contradicts path org "${pathOrgId}" for ${snapshot.ref.path} — using path`,
    );
  }
  const organizationId = pathOrgId ?? data.organizationId;

  if (!organizationId) {
    console.warn(
      `[toMembership] Skipping membership doc ${snapshot.ref.path} — no resolvable organizationId`,
    );
    return null;
  }

  return {
    ...data,
    organizationId,
    memberId: data.memberId || snapshot.id,
  };
}
